import { Divider, Flex, List, Popover, Table, Tag } from 'antd'
import { TableColumnsType } from 'antd/lib'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { useStocksServiceGetInternalIssuanceListEndpoint } from 'src/api/queries'
import {
  InternalInventoryDocumentState,
  InventoryDocumentItemResponse
} from 'src/api/requests'
import { PageBreadcrumbs } from 'src/components'
import { SelectedItemsState } from 'src/features/stock/types'
import { getStatus } from 'src/features/stock/utils'
import { LocalIssuanceFilters } from 'src/features/stock-local/components'
import { useTablePagination } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

dayjs.extend(customParseFormat)

const VISIBLE_TAG_COUNT = 2

const MapValuesToIds = (data: SelectedItemsState) => {
  return {
    roleIds: data?.roleIds?.values,
    fromDate: data?.inDate?.values?.[0],
    toDate: data?.inDate?.values?.[1],
    searchTerm: data?.search?.values?.[0],
    states: data?.states?.values as InternalInventoryDocumentState[]
  }
}

export const LocalIssuancesPage = () => {
  const [selectedItems, setSelectedItems] = useState<SelectedItemsState>({})

  const { t } = useLocales()
  const pagination = useTablePagination()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...MapValuesToIds(selectedItems)
  }

  const { data, isLoading } = useStocksServiceGetInternalIssuanceListEndpoint(
    queryParams,
    [queryParams]
  )

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'documentDate',
        title: t('stock_administration.release_date'),
        render: ({ documentDate }) =>
          dayjs(documentDate).format(dayMonthYearBackSlash)
      },
      {
        key: 'inquiryItems',
        title: t('stock_applications.consumables'),
        render: ({ items }: { items: InventoryDocumentItemResponse[] }) => {
          if (!items?.length) return '-'

          const extraCount = items?.length - VISIBLE_TAG_COUNT

          return (
            <Flex>
              <List
                itemLayout="horizontal"
                dataSource={items}
                renderItem={(item, index) =>
                  index < VISIBLE_TAG_COUNT && (
                    <Tag
                      key={item?.id}
                    >{`${item?.name} (${item?.quantity})`}</Tag>
                  )
                }
              />
              <Popover
                content={
                  <Flex vertical gap={4}>
                    {items?.slice(VISIBLE_TAG_COUNT).map((item) => (
                      <Tag
                        key={item?.id}
                      >{`${item?.name} (${item?.quantity})`}</Tag>
                    ))}
                  </Flex>
                }
              >
                {extraCount > 0 && <Tag className="pointer">+{extraCount}</Tag>}
              </Popover>
            </Flex>
          )
        }
      },
      {
        key: 'state',
        title: t('form.status'),
        render: ({ state }) => {
          return (
            <Tag color={getStatus(t, state).color} key={state}>
              {getStatus(t, state).text}
            </Tag>
          )
        }
      },
      {
        key: 'details',
        align: 'center',
        width: 103,
        render: ({ id }) => {
          return (
            <Link to={paths.stockLocal.issuance.issuanceDetails(id)}>
              {t('common.details')}
            </Link>
          )
        }
      }
    ],
    [t]
  )

  return (
    <PageBreadcrumbs
      title={t('menu.issuance')}
      items={[
        { title: t('menu.consumable_medicaments') },
        { title: t('menu.stock') }
      ]}
    >
      <Flex gap={16}>
        <LocalIssuanceFilters
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
        />
      </Flex>

      <Divider />

      <Flex vertical gap={24}>
        <Table
          rowKey="id"
          columns={columns}
          loading={isLoading}
          dataSource={data?.data}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>
    </PageBreadcrumbs>
  )
}
